import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight, Gamepad2, Loader2, ShieldAlert, TicketPercent, Wallet,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { usePromoTelegram } from '../hooks/usePromoTelegram';
import PromoTelegramGate from '../components/PromoTelegramGate';
import client from '../lib/api';
import { accountHook } from '../lib/domHooks';

const PAGE_SIZE = 20;

const dateTime = (value) => {
  const date = new Date(value || 0);
  return Number.isFinite(date.getTime()) && date.getTime() > 0
    ? date.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
    : '—';
};

// Kurulum "aktif çevrimi/bakiyesi olan kod kullanamaz" kuralını açmış olabilir
// (Pazarlama & Bonus → Promosyon Kodları → Kullanım Kuralları). Kapı sunucudadır;
// burada yalnızca sebebi önden yazılır — kapalı bir kapının önünde açık duran
// kod alanı, oyuncunun kodu girip anlamadığı bir hata almasına yol açıyordu.
const PromoBlockedNotice = ({ reason }) => (
  <div className="pusu-card mb-6 flex items-start gap-3 border-amber-500/30 p-4 md:p-5">
    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-500/10 text-amber-300">
      <ShieldAlert size={20} />
    </div>
    <div className="min-w-0">
      <h2 className="font-black text-amber-200">Kod şu anda kullanılamıyor</h2>
      <p className="mt-1 text-sm text-gray-400">{reason}</p>
    </div>
  </div>
);

const PromoCodeEntry = ({ code, setCode, redeeming, onSubmit }) => (
  <form onSubmit={onSubmit} className="pusu-card p-4 md:p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4">
    <div className="w-10 h-10 shrink-0 rounded-xl grid place-items-center bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]">
      <TicketPercent size={20} />
    </div>
    <div className="md:min-w-56">
      <h2 className="font-black gold-text">Promosyon Kodunuz mu var?</h2>
      <p className="text-xs text-gray-500 mt-1">
        Kodunuzu girin, uygun ödül anında hesabınıza tanımlansın.
      </p>
    </div>
    <div className="flex flex-1 gap-2 md:ml-auto">
      <input
        value={code}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
        className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-4 h-11 text-sm font-mono uppercase outline-none focus:border-[var(--pusu-gold)]/60"
        placeholder="PROMO KODU"
        maxLength={64}
      />
      <button type="submit" disabled={redeeming || !code.trim()} className="gold-btn px-4 md:px-6 h-11 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
        {redeeming ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        <span className="hidden sm:inline">Kodu Kullan</span>
      </button>
    </div>
  </form>
);

// Ödülün yazımı kullanım satırından türer: nakit kodda tutar, freespin kodda
// spin adedi. Freespin satırındaki `reward_amount` tur başına sağlayıcı
// bahsidir, oyuncunun cebine giren bir para değildir — bu yüzden yazılmaz.
const rewardText = (item) => (
  item.reward_type === 'freespin'
    ? `${Number(item.spin_count || 0)} freespin`
    : `+${Number(item.reward_amount || 0).toFixed(2)} ₺`
);

const RedemptionRow = ({ item }) => {
  const freespin = item.reward_type === 'freespin';
  const Icon = freespin ? Gamepad2 : Wallet;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-black/20 p-4">
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${freespin ? 'bg-sky-500/10 text-sky-300' : 'bg-emerald-500/10 text-emerald-300'}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm font-bold text-white break-all">
          {item.code}
        </div>
        <div className="mt-0.5 text-xs text-gray-500">
          {dateTime(item.created_at)}
        </div>
      </div>
      <div className={`shrink-0 text-sm font-bold ${freespin ? 'text-sky-300' : 'text-emerald-400'}`}>
        {rewardText(item)}
      </div>
    </div>
  );
};

/** Promosyon kodu ekranı: kod girişi ve oyuncunun kendi kullanım geçmişi.
 *
 * Bonus kampanyaları, çevrim ve freespin hakları burada değil Promosyonlar
 * (bonus) sayfasındadır — iki iş tek sayfada durduğunda kod alanı kampanya
 * listesinin altında kalıyor ve bulunamıyordu.
 *
 * Liste yalnızca **kullanılmış** kodları gösterir: henüz girilmemiş kodları
 * listelemek hedefli dağıtılan bir kampanyayı herkese açardı (uç da onları
 * döndürmez).
 */
const PromoCode = ({ embedded = false }) => {
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { toast } = useToast();
  const { user, setWalletBalances } = useAuth();
  const settings = useSettings();
  const userId = user?.id;
  const telegramRequired = settings.promo_require_telegram === true;
  const telegram = usePromoTelegram(telegramRequired);
  // Kurulumun kod kullanım kapısı. Sebep sunucuda üretilir: kuralı istemcide
  // yeniden yazmak, iki yerin ayrışıp ekranda bir şey yazarken isteğin başka
  // bir şey söylemesi demekti.
  const [blockReason, setBlockReason] = useState('');

  const refreshEligibility = useCallback(async () => {
    if (!userId) {
      setBlockReason('');
      return;
    }
    try {
      const { data } = await client.get('/promos/eligibility');
      setBlockReason(data?.eligible ? '' : (data?.reason || ''));
    } catch {
      // Kapı sunucudadır; uygunluk okunamadıysa alan açık bırakılır ve
      // karar redeem isteğinde verilir.
      setBlockReason('');
    }
  }, [userId]);

  useEffect(() => { refreshEligibility(); }, [refreshEligibility]);

  const loadHistory = useCallback(async (skip = 0) => {
    if (!userId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.get('/promos/redemptions/me', {
        params: { skip, limit: PAGE_SIZE },
      });
      setItems((previous) => (
        skip ? [...previous, ...(data.items || [])] : (data.items || [])
      ));
      setTotal(Number(data.total || 0));
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadHistory(0); }, [loadHistory]);

  const redeemCode = async (event) => {
    event.preventDefault();
    if (!user) {
      toast({ title: 'Giriş gerekli', description: 'Promosyon kodunu kullanmak için üye girişi yapın' });
      return;
    }
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await client.post('/promos/redeem', { code: code.trim() });
      setWalletBalances(data);
      setCode('');
      toast({
        title: 'Promosyon uygulandı',
        description: data.reward_type === 'cash'
          ? `Bakiyenize ${Number(data.reward_amount).toFixed(2)} ₺ eklendi`
          : `${data.spin_count} freespin hesabınıza tanımlandı`,
      });
      loadHistory(0);
      refreshEligibility();
    } catch (error) {
      if (telegramRequired && [403, 503].includes(error.response?.status)) {
        telegram.refresh();
      }
      if (error.response?.status === 409) {
        // Kapı kapanmış olabilir: sebebi ekrana da yazalım, yoksa oyuncu
        // aynı kodu tekrar tekrar dener.
        refreshEligibility();
      }
      toast({
        title: 'Kod kullanılamadı',
        description: error.response?.data?.detail || 'Lütfen kodu kontrol edip tekrar deneyin',
      });
    } finally {
      setRedeeming(false);
    }
  };

  // Hesap kabuğunun içinde (bkz. lib/accountMenu.js
  // `PROMO_CODE_IN_ACCOUNT_THEMES`) sayfanın kendi zemini ve kenar boşluğu
  // çizilmez: onları kabuk verir, ikinci kez verilirse içerik sütunu daralırdı.
  const outerProps = embedded
    ? { className: 'promo-code-embedded' }
    : { className: 'pusu-body-gradient' };
  const innerProps = embedded
    ? { className: 'pusu-panel p-4 md:p-6', ...accountHook('panel') }
    : { className: 'max-w-4xl mx-auto px-4 py-8' };

  return (
    <div {...outerProps}>
      <div {...innerProps}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--pusu-gold-rgb),0.12)' }}>
            <TicketPercent size={22} className="text-[var(--pusu-gold)]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black gold-text">Promosyon Kodu</h1>
            <p className="text-sm text-gray-400">
              Kodunuzu kullanın, geçmiş kullanımlarınızı buradan takip edin
            </p>
          </div>
        </div>

        {!settings.settingsLoaded ? (
          <PromoTelegramGate user={user} loading status={null} purpose="promo" />
        ) : blockReason ? (
          <PromoBlockedNotice reason={blockReason} />
        ) : !telegramRequired || telegram.status?.member ? (
          <PromoCodeEntry
            code={code}
            setCode={setCode}
            redeeming={redeeming}
            onSubmit={redeemCode}
          />
        ) : (
          <PromoTelegramGate
            user={user}
            loading={telegram.loading}
            status={telegram.status}
            linking={telegram.linking}
            linkUrl={telegram.linkUrl}
            onLink={telegram.link}
            onRefresh={telegram.refresh}
            purpose="promo"
          />
        )}

        <section className="pusu-card p-5" aria-labelledby="promo-history-title">
          <h2 id="promo-history-title" className="font-black gold-text">
            Kullandığım Kodlar
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Yalnızca kullandığınız kodlar listelenir.
          </p>

          {!user ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Kullandığınız kodları görmek için üye girişi yapın.
            </div>
          ) : loading && !items.length ? (
            <div className="py-10 text-center text-sm text-gray-400">
              <Loader2 className="mr-2 inline animate-spin" size={16} />
              Yükleniyor…
            </div>
          ) : loadError ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Kullanım geçmişi alınamadı. Sayfayı yenileyip tekrar deneyin.
            </div>
          ) : !items.length ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Henüz bir promosyon kodu kullanmadınız.
            </div>
          ) : (
            <>
              <div className="mt-4 space-y-2">
                {items.map((item) => (
                  <RedemptionRow key={item.id} item={item} />
                ))}
              </div>
              {items.length < total && (
                <button
                  type="button"
                  onClick={() => loadHistory(items.length)}
                  disabled={loading}
                  className="gold-outline-btn mt-4 w-full rounded-lg py-2.5 text-sm disabled:opacity-50"
                >
                  {loading ? 'Yükleniyor…' : 'Daha fazla göster'}
                </button>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default PromoCode;
